import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "reactstrap";
import { confirmOrderDetails, previewOrderConfirmation } from "../../services/studioOrderApi";
import "./orders.scss";

type ConfirmView = {
  orderCode?: string;
  orgName?: string;
  clientConfirmation?: string;
  confirmed?: boolean;
};

const OrderConfirmPage = () => {
  const { token } = useParams<{ token: string }>();
  const [view, setView] = useState<ConfirmView | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setError("This confirmation link is not valid.");
      return;
    }
    previewOrderConfirmation(token)
      .then((res) => {
        if (cancelled) {
          return;
        }
        if (!res?.success) {
          setError(res?.message || "This confirmation link is not valid.");
          return;
        }
        setView(res.data || {});
      })
      .catch(() => {
        if (!cancelled) {
          setError("Could not open this confirmation.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const confirm = async () => {
    if (!token || busy) {
      return;
    }
    setBusy(true);
    try {
      const res = await confirmOrderDetails(token);
      if (!res?.success) {
        setError(res?.message || "Could not confirm this order.");
        return;
      }
      setView(res.data || {});
      setError("");
    } catch {
      setError("Could not confirm this order.");
    } finally {
      setBusy(false);
    }
  };

  const code = view?.orderCode || "this order";
  const studio = view?.orgName ? ` from ${view.orgName}` : "";

  return (
    <div className="order-confirm-page">
      <h1>Order confirmation</h1>
      {error ? <p>{error}</p> : null}
      {!error && !view ? <p>Loading…</p> : null}
      {view?.confirmed ? (
        <p>
          {view.clientConfirmation === "AUTO"
            ? `${code}${studio} was confirmed automatically.`
            : `${code}${studio} is confirmed.`}
        </p>
      ) : null}
      {view && !view.confirmed ? (
        <>
          <p>Please read the email for {code}{studio} and confirm the details.</p>
          <Button color="primary" disabled={busy} onClick={confirm}>
            {busy ? "Confirming…" : "Confirm these details"}
          </Button>
        </>
      ) : null}
    </div>
  );
};

export default OrderConfirmPage;
